import { useCallback, useEffect, useState } from "react";
import { RefreshCw, ScrollText } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
};

const TYPE_BADGE = {
  ocr_test: ["OCR", "border-slate-400 text-slate-600"],
  ocr_upload: ["OCR Intake", "border-slate-400 text-slate-600"],
  ocr_rerun: ["OCR Re-run", "border-slate-400 text-slate-600"],
  pii_reveal: ["PII Reveal", "border-red-400 text-red-700"],
  unicorn_assessment: ["Advisor", "border-teal-600/50 text-teal-800"],
};

export default function AuditTable({ refreshKey }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/gateway/audit?limit=25");
      setRecords(data.records || []);
    } catch {
      // silent: table simply stays stale
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load, refreshKey]);

  return (
    <Card className="rounded-none border-border shadow-sm" data-testid="audit-log-card">
      <CardHeader className="border-b border-border pb-4 flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-teal-600" strokeWidth={2.2} />
          <CardTitle className="text-sm font-semibold uppercase tracking-[0.12em] text-primary">
            Gateway Audit Log
          </CardTitle>
          <span className="text-[11px] font-mono text-muted-foreground ml-1">
            masked payloads only — raw PII never stored
          </span>
        </div>
        <Button
          data-testid="audit-refresh-btn"
          variant="outline"
          size="sm"
          onClick={load}
          className="rounded-none h-8 text-[12px] border-border"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table data-testid="audit-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <TableHead className="text-[11px] uppercase tracking-[0.1em] pl-6">Timestamp</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.1em]">Type</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.1em]">Model</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.1em]">Entities Masked</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.1em] text-right">Latency</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.1em] text-right pr-6">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-[13px] text-muted-foreground py-10" data-testid="audit-empty-state">
                  No gateway calls yet. Run a test above to populate the audit trail.
                </TableCell>
              </TableRow>
            )}
            {records.map((r) => (
              <TableRow key={r.id} className="border-border hover:bg-slate-50/70" data-testid={`audit-row-${r.id}`}>
                <TableCell className="font-mono text-[12px] text-slate-600 pl-6 whitespace-nowrap">
                  {fmtTime(r.timestamp)}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`rounded-none text-[10px] font-mono uppercase ${
                      (TYPE_BADGE[r.request_type] || ["", "border-teal-600/50 text-teal-800"])[1]
                    }`}
                  >
                    {(TYPE_BADGE[r.request_type] || ["Gateway"])[0]}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-[12px] text-slate-600">{r.model}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(r.entity_counts || {}).map(([type, count]) => (
                      <span
                        key={type}
                        className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 border border-border"
                      >
                        {type}×{count}
                      </span>
                    ))}
                    {Object.keys(r.entity_counts || {}).length === 0 && (
                      <span className="text-[11px] text-muted-foreground">—</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono text-[12px] text-slate-600">
                  {r.latency_ms}ms
                </TableCell>
                <TableCell className="text-right pr-6">
                  <Badge
                    className={`rounded-none text-[10px] font-mono uppercase ${
                      r.success
                        ? "bg-teal-700 hover:bg-teal-700 text-white"
                        : "bg-red-600 hover:bg-red-600 text-white"
                    }`}
                  >
                    {r.success ? "OK" : "FAIL"}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
